import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X, Send, Terminal } from 'lucide-react';
const suggestions = ['How can AI help hotels?', 'Show conveyancing use cases', 'What makes Aijentik different?'];
const responses: Record<string, string> = {
  'How can AI help hotels?': 'Aijentik Hospitality automates reservation management, handles guest messaging instantly, and identifies intelligent upsell opportunities. This typically results in 40% faster response times and higher guest satisfaction scores.',
  'Show conveyancing use cases': 'For conveyancing, our agents automate document workflows, provide proactive client updates, and handle manual settlement preparation. This reduces administrative time by up to 60%, allowing your team to focus on complex legal work.',
  'What makes Aijentik different?': "Unlike generic AI wrappers, Aijentik builds vertical-specific agents. They understand your industry's operational context, compliance requirements, and specific workflows out of the box. We focus on execution and business logic, not just chat.",
  default: "I'm the Aijentik Navigator. I can help you explore our vertical AI solutions for Hospitality, Conveyancing, Renewables, Maritime, and Field Service. What industry are you interested in?"
};
export function Navigator() {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [chatHistory, setChatHistory] = useState<{
    role: 'user' | 'ai';
    content: string;
  }[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  // Show navigator after scrolling past hero
  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > window.innerHeight * 0.5);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const handleSubmit = (query: string) => {
    if (!query.trim()) return;
    setChatHistory(prev => [...prev, {
      role: 'user',
      content: query
    }]);
    setInputValue('');
    setIsTyping(true);
    // Simulate AI response delay
    setTimeout(() => {
      const response = responses[query] || responses.default;
      setChatHistory(prev => [...prev, {
        role: 'ai',
        content: response
      }]);
      setIsTyping(false);
    }, 1000);
  };
  return <AnimatePresence data-id="element-344">
      {isVisible && <motion.div initial={{
      y: 100,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} exit={{
      y: 100,
      opacity: 0
    }} className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-full max-w-2xl px-4" data-id="element-345">
          {isOpen ? <motion.div layoutId="navigator-container" className="bg-[#111] border border-white/10 rounded-2xl shadow-2xl overflow-hidden flex flex-col h-[400px]" data-id="element-346">
              <div className="flex items-center justify-between p-4 border-b border-white/10 bg-[#0A0A0A]" data-id="element-347">
                <div className="flex items-center gap-2 text-white font-medium" data-id="element-348">
                  <Terminal className="w-4 h-4 text-brand-orange" data-id="element-349" />
                  Aijentik Navigator
                </div>
                <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-white transition-colors" data-id="element-350">
                  <X className="w-5 h-5" data-id="element-351" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-352">
                {chatHistory.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-center space-y-6" data-id="element-353">
                    <Sparkles className="w-8 h-8 text-brand-orange/50" data-id="element-354" />
                    <div className="flex flex-wrap justify-center gap-2" data-id="element-355">
                      {suggestions.map((suggestion, i) => <button key={i} onClick={() => handleSubmit(suggestion)} className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/5 rounded-full text-sm text-gray-300 transition-colors" data-id="element-356">
                          {suggestion}
                        </button>)}
                    </div>
                  </div> : <div className="space-y-4" data-id="element-357">
                    {chatHistory.map((msg, i) => <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-358">
                        <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${msg.role === 'user' ? 'bg-brand-orange text-white' : 'bg-white/5 text-gray-200 border border-white/5'}`} data-id="element-359">
                          {msg.content}
                        </div>
                      </div>)}
                    {isTyping && <div className="flex justify-start" data-id="element-360">
                        <div className="bg-white/5 border border-white/5 rounded-2xl px-4 py-3 flex gap-1" data-id="element-361">
                          <motion.div animate={{
                  y: [0, -5, 0]
                }} transition={{
                  repeat: Infinity,
                  duration: 0.6,
                  delay: 0
                }} className="w-1.5 h-1.5 bg-gray-500 rounded-full" data-id="element-362" />
                          <motion.div animate={{
                  y: [0, -5, 0]
                }} transition={{
                  repeat: Infinity,
                  duration: 0.6,
                  delay: 0.2
                }} className="w-1.5 h-1.5 bg-gray-500 rounded-full" data-id="element-363" />
                          <motion.div animate={{
                  y: [0, -5, 0]
                }} transition={{
                  repeat: Infinity,
                  duration: 0.6,
                  delay: 0.4
                }} className="w-1.5 h-1.5 bg-gray-500 rounded-full" data-id="element-364" />
                        </div>
                      </div>}
                  </div>}
              </div>

              <div className="p-4 bg-[#0A0A0A] border-t border-white/10" data-id="element-365">
                <form onSubmit={e => {
            e.preventDefault();
            handleSubmit(inputValue);
          }} className="relative flex items-center" data-id="element-366">
                  <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} placeholder="Ask Aijentik anything..." className="w-full bg-white/5 border border-white/10 rounded-full pl-4 pr-12 py-3 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-brand-orange/50 transition-colors" data-id="element-367" />
                  <button type="submit" disabled={!inputValue.trim() || isTyping} className="absolute right-2 p-2 text-brand-orange hover:text-brand-deep-orange disabled:opacity-50 transition-colors" data-id="element-368">
                    <Send className="w-4 h-4" data-id="element-369" />
                  </button>
                </form>
              </div>
            </motion.div> : <motion.div layoutId="navigator-container" onClick={() => setIsOpen(true)} className="bg-[#111]/90 backdrop-blur-md border border-white/10 rounded-full p-2 flex items-center gap-3 cursor-pointer hover:border-white/20 transition-colors shadow-2xl" data-id="element-370">
              <div className="w-10 h-10 rounded-full bg-brand-orange/10 flex items-center justify-center" data-id="element-371">
                <Sparkles className="w-5 h-5 text-brand-orange" data-id="element-372" />
              </div>
              <span className="text-gray-400 text-sm font-medium pr-6" data-id="element-373">
                Ask Aijentik anything...
              </span>
            </motion.div>}
        </motion.div>}
    </AnimatePresence>;
}